import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IPost, IPostComment } from "../services/feedAPI";

type FeedState = {
  publicPosts: IPost[];
  privatePosts: IPost[];
  mentionPosts: IPost[];
  activityPosts: IPost[];
  otherUserActivityPosts: IPost[];
  rewritePublicPosts: boolean;
  currentPostDetail: (IPost & { comments: IPostComment[] }) | null;
  currentPostLikes: number | null;
};

const togglePostLikeCb = (post: IPost, postId: string) => {
  if (post._id === postId) {
    let updatedAlreadyLiked;
    let updatedLikeCount;
    let updatedBoosterLikes;
    if (post.alreadyLiked) {
      updatedAlreadyLiked = false;
      updatedLikeCount = post.likeCount - 1;
      updatedBoosterLikes = post?.boosterLikes! - 1;
    } else {
      updatedAlreadyLiked = true;
      updatedLikeCount = post.likeCount + 1;
      updatedBoosterLikes = post?.boosterLikes! + 1;
    }
    return {
      ...post,
      likeCount: updatedLikeCount,
      boosterLikes: updatedBoosterLikes,
      alreadyLiked: updatedAlreadyLiked,
    };
  }
  return post;
};

const hideAuthorPosts = (post: IPost, authorId: string) => {
  if (post.author._id === authorId) {
    return {
      ...post,
      hidden: true,
    };
  }
  return post;
};

const addCommentCb = (post: IPost, postId: string) => {
  if (post._id === postId) {
    const updatedCommentCount = post.commentCount + 1;
    return {
      ...post,
      commentCount: updatedCommentCount,
    };
  }
  return post;
};

const deleteCommentCb = (post: IPost, postId: string) => {
  if (post._id === postId) {
    const updatedCommentCount = post.commentCount - 1;
    return {
      ...post,
      commentCount: updatedCommentCount,
    };
  }
  return post;
};

const editPostCb = (post: IPost, updatedPost: IPost) => {
  if (post._id === updatedPost?._id) {
    return updatedPost;
  }
  return post;
};

const deletePostCb = (post: IPost, postId: string) => {
  return post._id !== postId;
};

const superconnectCb = (post: IPost, authorId: string) => {
  if (post?.author?._id === authorId) {
    return {
      ...post,
      author: {
        ...post.author,
        connected: true,
      },
    };
  }
  return post;
};

const setPollVoteCb = (post: IPost, postId: string, pollOptionId: string) => {
  if (post._id === postId) {
    const updatedPollOptions = post?.pollOptions?.map((option) => {
      if (option._id === pollOptionId) {
        return {
          ...option,
          voteCount: option.voteCount + 1,
          voted: true,
        };
      }
      return option;
    });
    return {
      ...post,
      alreadyVoted: true,
      totalVoteCount: post?.totalVoteCount! + 1,
      pollOptions: updatedPollOptions,
    };
  }
  return post;
};

const feedSlice = createSlice({
  name: "feed",
  initialState: {
    publicPosts: [],
    privatePosts: [],
    mentionPosts: [],
    activityPosts: [],
    otherUserActivityPosts: [],
    rewritePublicPosts: false,
    currentPostDetail: null,
    currentPostLikes: null,
  } as FeedState,
  reducers: {
    setPublicPosts: (
      state,
      {
        payload: { posts, reset = false },
      }: PayloadAction<{
        posts: IPost[];
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.publicPosts = posts;
      } else {
        // const mergedPosts = [...state.publicPosts, ...posts];
        let postIds = new Set(state.publicPosts.map((post) => post._id));
        let mergedPosts = [
          ...state.publicPosts,
          ...posts.filter((post) => !postIds.has(post._id)),
        ];
        state.publicPosts = mergedPosts;
      }
    },
    setPrivatePosts: (
      state,
      {
        payload: { posts, reset = false },
      }: PayloadAction<{
        posts: IPost[];
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.privatePosts = posts;
      } else {
        // const mergedPosts = [...state.privatePosts, ...posts];
        let postIds = new Set(state.privatePosts.map((post) => post._id));
        let mergedPosts = [
          ...state.privatePosts,
          ...posts.filter((post) => !postIds.has(post._id)),
        ];
        state.privatePosts = mergedPosts;
      }
    },
    setMentionPosts: (
      state,
      {
        payload: { posts, reset = false },
      }: PayloadAction<{
        posts: IPost[];
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.mentionPosts = posts;
      } else {
        // const mergedPosts = [...state.mentionPosts, ...posts];
        let postIds = new Set(state.mentionPosts.map((post) => post._id));
        let mergedPosts = [
          ...state.mentionPosts,
          ...posts.filter((post) => !postIds.has(post._id)),
        ];
        state.mentionPosts = mergedPosts;
      }
    },
    setActivityPosts: (
      state,
      {
        payload: { posts, reset = false },
      }: PayloadAction<{
        posts: IPost[];
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.activityPosts = posts;
      } else {
        // const mergedPosts = [...state.activityPosts, ...posts];
        let postIds = new Set(state.activityPosts.map((post) => post._id));
        let mergedPosts = [
          ...state.activityPosts,
          ...posts.filter((post) => !postIds.has(post._id)),
        ];
        state.activityPosts = mergedPosts;
      }
    },
    setOtherUserActivityPosts: (
      state,
      {
        payload: { posts, reset = false },
      }: PayloadAction<{
        posts: IPost[];
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.otherUserActivityPosts = posts;
      } else {
        // const mergedPosts = [...state.otherUserActivityPosts, ...posts];
        let postIds = new Set(
          state.otherUserActivityPosts.map((post) => post._id)
        );
        let mergedPosts = [
          ...state.otherUserActivityPosts,
          ...posts.filter((post) => !postIds.has(post._id)),
        ];
        state.otherUserActivityPosts = mergedPosts;
      }
    },
    setCurrentPostDetail: (
      state,
      {
        payload: { post, reset = false },
      }: PayloadAction<{
        post: IPost & { comments: IPostComment[] };
        reset?: boolean;
      }>
    ) => {
      if (reset) {
        state.currentPostDetail = post;
      } else {
        state.currentPostDetail = post;
      }
    },
    setCurrentPostLikes: (
      state,
      {
        payload: { likes },
      }: PayloadAction<{
        likes: number;
      }>
    ) => {
      state.currentPostLikes = likes;
    },
    togglePostLike: (
      state,
      {
        payload: { postId, likes },
      }: PayloadAction<{ postId: string; likes?: number }>
    ) => {
      const updatedPublicPosts = state.publicPosts.map((post) =>
        togglePostLikeCb(post, postId)
      );
      const updatedPrivatePosts = state.privatePosts.map((post) =>
        togglePostLikeCb(post, postId)
      );
      const updatedMentionPosts = state.mentionPosts.map((post) =>
        togglePostLikeCb(post, postId)
      );
      const updatedActivityPosts = state.activityPosts.map((post) =>
        togglePostLikeCb(post, postId)
      );
      const updatedOtherUserActivityPosts = state.otherUserActivityPosts.map(
        (post) => togglePostLikeCb(post, postId)
      );

      state.publicPosts = updatedPublicPosts;
      state.privatePosts = updatedPrivatePosts;
      state.mentionPosts = updatedMentionPosts;
      state.activityPosts = updatedActivityPosts;
      state.otherUserActivityPosts = updatedOtherUserActivityPosts;
      state.currentPostLikes = likes ? likes : null;
    },
    muteAuthorPosts: (
      state,
      { payload: { authorId } }: PayloadAction<{ authorId: string }>
    ) => {
      const updatedPublicPosts = state.publicPosts.map((post) =>
        hideAuthorPosts(post, authorId)
      );
      const updatedPrivatePosts = state.privatePosts.map((post) =>
        hideAuthorPosts(post, authorId)
      );
      const updatedMentionPosts = state.mentionPosts.map((post) =>
        hideAuthorPosts(post, authorId)
      );
      const updatedActivityPosts = state.activityPosts.map((post) =>
        hideAuthorPosts(post, authorId)
      );
      const updatedOtherUserActivityPosts = state.otherUserActivityPosts.map(
        (post) => hideAuthorPosts(post, authorId)
      );

      state.publicPosts = updatedPublicPosts;
      state.privatePosts = updatedPrivatePosts;
      state.mentionPosts = updatedMentionPosts;
      state.activityPosts = updatedActivityPosts;
      state.otherUserActivityPosts = updatedOtherUserActivityPosts;
    },
    addComment: (
      state,
      { payload: { postId } }: PayloadAction<{ postId: string }>
    ) => {
      const updatedPublicPosts = state.publicPosts.map((post) =>
        addCommentCb(post, postId)
      );
      const updatedPrivatePosts = state.privatePosts.map((post) =>
        addCommentCb(post, postId)
      );
      const updatedMentionPosts = state.mentionPosts.map((post) =>
        addCommentCb(post, postId)
      );
      const updatedActivityPosts = state.activityPosts.map((post) =>
        addCommentCb(post, postId)
      );
      const updatedOtherUserActivityPosts = state.otherUserActivityPosts.map(
        (post) => addCommentCb(post, postId)
      );
      state.publicPosts = updatedPublicPosts;
      state.privatePosts = updatedPrivatePosts;
      state.mentionPosts = updatedMentionPosts;
      state.activityPosts = updatedActivityPosts;
      state.otherUserActivityPosts = updatedOtherUserActivityPosts;
    },
    deleteComment: (
      state,
      { payload: { postId } }: PayloadAction<{ postId: string }>
    ) => {
      const updatedPublicPosts = state.publicPosts.map((post) =>
        deleteCommentCb(post, postId)
      );
      const updatedPrivatePosts = state.privatePosts.map((post) =>
        deleteCommentCb(post, postId)
      );
      const updatedMentionPosts = state.mentionPosts.map((post) =>
        deleteCommentCb(post, postId)
      );
      const updatedActivityPosts = state.activityPosts.map((post) =>
        deleteCommentCb(post, postId)
      );
      const updatedOtherUserActivityPosts = state.otherUserActivityPosts.map(
        (post) => deleteCommentCb(post, postId)
      );
      state.publicPosts = updatedPublicPosts;
      state.privatePosts = updatedPrivatePosts;
      state.mentionPosts = updatedMentionPosts;
      state.activityPosts = updatedActivityPosts;
      state.otherUserActivityPosts = updatedOtherUserActivityPosts;
    },
    editPost: (
      state,
      { payload: { updatedPost } }: PayloadAction<{ updatedPost: IPost }>
    ) => {
      const updatedPublicPosts = state.publicPosts.map((post) =>
        editPostCb(post, updatedPost)
      );
      const updatedPrivatePosts = state.privatePosts.map((post) =>
        editPostCb(post, updatedPost)
      );
      const updatedMentionPosts = state.mentionPosts.map((post) =>
        editPostCb(post, updatedPost)
      );
      const updatedActivityPosts = state.activityPosts.map((post) =>
        editPostCb(post, updatedPost)
      );
      const updatedOtherUserActivityPosts = state.otherUserActivityPosts.map(
        (post) => editPostCb(post, updatedPost)
      );
      state.publicPosts = updatedPublicPosts;
      state.privatePosts = updatedPrivatePosts;
      state.mentionPosts = updatedMentionPosts;
      state.activityPosts = updatedActivityPosts;
      state.otherUserActivityPosts = updatedOtherUserActivityPosts;
    },
    deletePost: (
      state,
      { payload: { postId } }: PayloadAction<{ postId: string }>
    ) => {
      const updatedPublicPosts = state.publicPosts.filter((post) =>
        deletePostCb(post, postId)
      );
      const updatedPrivatePosts = state.privatePosts.filter((post) =>
        deletePostCb(post, postId)
      );
      const updatedMentionPosts = state.mentionPosts.filter((post) =>
        deletePostCb(post, postId)
      );
      const updatedActivityPosts = state.activityPosts.filter((post) =>
        deletePostCb(post, postId)
      );
      const updatedOtherUserActivityPosts = state.otherUserActivityPosts.filter(
        (post) => deletePostCb(post, postId)
      );
      state.publicPosts = updatedPublicPosts;
      state.privatePosts = updatedPrivatePosts;
      state.mentionPosts = updatedMentionPosts;
      state.activityPosts = updatedActivityPosts;
      state.otherUserActivityPosts = updatedOtherUserActivityPosts;
    },
    removeBookmark: (
      state,
      { payload: { postId } }: PayloadAction<{ postId: string }>
    ) => {
      const updatedActivityPosts = state.activityPosts.filter((post) =>
        deletePostCb(post, postId)
      );
      state.activityPosts = updatedActivityPosts;
    },
    setAuthorConnectionStatus: (
      state,
      { payload: { authorId } }: PayloadAction<{ authorId: string }>
    ) => {
      const updatedPublicPosts = state.publicPosts.map((post) =>
        superconnectCb(post, authorId)
      );
      const updatedPrivatePosts = state.privatePosts.map((post) =>
        superconnectCb(post, authorId)
      );
      const updatedMentionPosts = state.mentionPosts.map((post) =>
        superconnectCb(post, authorId)
      );
      const updatedActivityPosts = state.activityPosts.map((post) =>
        superconnectCb(post, authorId)
      );
      const updatedOtherUserActivityPosts = state.otherUserActivityPosts.map(
        (post) => superconnectCb(post, authorId)
      );
      state.publicPosts = updatedPublicPosts;
      state.privatePosts = updatedPrivatePosts;
      state.mentionPosts = updatedMentionPosts;
      state.activityPosts = updatedActivityPosts;
      state.otherUserActivityPosts = updatedOtherUserActivityPosts;
    },
    setCreatedPostInFeed: (
      state,
      {
        payload: { post },
      }: PayloadAction<{
        post: IPost;
      }>
    ) => {
      if (post) {
        const updatedActivityPosts = [post, ...state.activityPosts];
        const updatedPublicPosts = [post, ...state.publicPosts];
        const updatedPrivatePosts = [post, ...state.privatePosts];
        state.activityPosts = updatedActivityPosts;
        state.publicPosts = updatedPublicPosts;
        state.privatePosts = updatedPrivatePosts;
      }
    },
    setPollVote: (
      state,
      {
        payload: { postId, pollOptionId },
      }: PayloadAction<{ postId: string; pollOptionId: string }>
    ) => {
      const updatedPublicPosts = state.publicPosts.map((post) =>
        setPollVoteCb(post, postId, pollOptionId)
      );
      const updatedPrivatePosts = state.privatePosts.map((post) =>
        setPollVoteCb(post, postId, pollOptionId)
      );
      const updatedMentionPosts = state.mentionPosts.map((post) =>
        setPollVoteCb(post, postId, pollOptionId)
      );
      const updatedActivityPosts = state.activityPosts.map((post) =>
        setPollVoteCb(post, postId, pollOptionId)
      );
      const updatedOtherUserActivityPosts = state.otherUserActivityPosts.map(
        (post) => setPollVoteCb(post, postId, pollOptionId)
      );
      state.publicPosts = updatedPublicPosts;
      state.privatePosts = updatedPrivatePosts;
      state.mentionPosts = updatedMentionPosts;
      state.activityPosts = updatedActivityPosts;
      state.otherUserActivityPosts = updatedOtherUserActivityPosts;
    },
    setRewritePublicPosts: (
      state,
      { payload: { value = false } }: PayloadAction<{ value: boolean }>
    ) => {
      state.rewritePublicPosts = value;
    },
  },
});

export const {
  setPublicPosts,
  setPrivatePosts,
  setMentionPosts,
  setActivityPosts,
  togglePostLike,
  deletePost,
  addComment,
  editPost,
  deleteComment,
  removeBookmark,
  setOtherUserActivityPosts,
  setAuthorConnectionStatus,
  setCreatedPostInFeed,
  setPollVote,
  muteAuthorPosts,
  setRewritePublicPosts,
  setCurrentPostDetail,
  setCurrentPostLikes,
} = feedSlice.actions;

export default feedSlice.reducer;
